import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, Save, X, Calculator, Building, MapPin, Percent } from 'lucide-react';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';
import { useScope } from '../contexts/ScopeContext';

interface TaxConfiguration {
  configId: string;
  enterpriseId: string;
  configType: 'ENTERPRISE' | 'ORG_UNIT' | 'STORE';
  entityId: string;
  entityName: string;
  taxRates: {
    salesTax?: number;
    cityTax?: number;
    countyTax?: number;
    stateTax?: number;
    specialTax?: number;
  };
  taxJurisdiction: string;
  taxableCategories: string[];
  exemptCategories: string[];
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  externalServiceConfig?: {
    provider: string;
    accountId: string;
    locationCode: string;
    companyCode: string;
    lastSync?: string;
  };
  inheritFrom?: string;
  overrides?: any;
  isInherited: boolean;
  effectiveDate: string;
  expirationDate?: string;
  validationStatus: 'VALID' | 'EXPIRED' | 'REQUIRES_UPDATE';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
}

interface Enterprise {
  enterpriseId: string;
  name: string;
}

interface Store {
  storeId: string;
  name: string;
  storeCode?: string;
  address?: any;
}

interface TaxManagementProps {
  user: any;
}

const TaxManagement: React.FC<TaxManagementProps> = ({ user }) => {
  const { getIdToken } = useAuth();
  const { enterpriseId: scopeEnterpriseId } = useScope();

  // State management
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState<string>('');
  const [userPickedEnterprise, setUserPickedEnterprise] = useState(false);

  // Follow the header enterprise selector until the user picks locally.
  useEffect(() => {
    if (userPickedEnterprise || !scopeEnterpriseId) return;
    if (selectedEnterprise === scopeEnterpriseId) return;
    if (enterprises.length > 0 && !enterprises.some((e) => e.enterpriseId === scopeEnterpriseId)) return;
    setSelectedEnterprise(scopeEnterpriseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEnterpriseId, userPickedEnterprise, enterprises]);
  const [taxConfigurations, setTaxConfigurations] = useState<TaxConfiguration[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingConfig, setEditingConfig] = useState<TaxConfiguration | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'configurations' | 'calculator'>('configurations');

  // Form state for creating/editing
  const [formData, setFormData] = useState({
    configType: 'STORE' as 'ENTERPRISE' | 'ORG_UNIT' | 'STORE',
    entityId: '',
    entityName: '',
    taxRates: {
      salesTax: 0,
      cityTax: 0,
      countyTax: 0,
      stateTax: 0,
      specialTax: 0
    },
    taxJurisdiction: '',
    taxableCategories: [] as string[],
    exemptCategories: [] as string[],
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    effectiveDate: new Date().toISOString().split('T')[0]
  });

  // Tax calculation state
  const [calculatorData, setCalculatorData] = useState({
    storeId: '',
    amount: 0,
    itemCategories: [] as string[]
  });
  const [calculationResult, setCalculationResult] = useState<any>(null);

  // Load initial data
  useEffect(() => {
    loadEnterprises();
  }, []);

  useEffect(() => {
    if (selectedEnterprise) {
      loadTaxConfigurations();
      loadStores();
    }
  }, [selectedEnterprise]);

  const loadEnterprises = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.enterprises(), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      // Handle both wrapped and direct responses
      const enterpriseList = data.data || data || [];
      setEnterprises(enterpriseList);
      if (enterpriseList.length > 0) {
        const preferred = scopeEnterpriseId && enterpriseList.some((e: Enterprise) => e.enterpriseId === scopeEnterpriseId)
          ? scopeEnterpriseId
          : enterpriseList[0].enterpriseId;
        setSelectedEnterprise(preferred);
      }
    } catch (error) {
      console.error('Error loading enterprises:', error);
    }
  };

  const loadTaxConfigurations = async () => {
    if (!selectedEnterprise) return;
    
    try {
      setLoading(true);
      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.taxEnterprise(selectedEnterprise), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      console.log('Tax configurations response:', data);
      
      // Handle both wrapped and direct responses, check for null/empty response
      let configList = [];
      if (data && data !== null && typeof data === 'object') {
        if (data.Message === null || data.Message === undefined) {
          // Comprehensive API returns {"Message": null} when no data
          console.log('Tax configurations API returned empty response - no configurations exist');
          configList = [];
        } else {
          configList = data.data || data || [];
        }
      }
      console.log('Setting tax configurations:', configList);
      setTaxConfigurations(configList);
    } catch (error) {
      console.error('Error loading tax configurations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStores = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.stores(), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      // Handle both wrapped and direct responses
      const storeList = data.data || data || [];
      setStores(storeList);
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const handleSaveConfiguration = async () => {
    if (!selectedEnterprise) return;
    
    try {
      setSaving(true);
      const token = await getIdToken();
      
      const configData = {
        ...formData,
        enterpriseId: selectedEnterprise,
        createdBy: user?.email || user?.getUsername?.() || 'system',
        updatedBy: user?.email || user?.getUsername?.() || 'system'
      };
      
      let response;
      
      if (editingConfig) {
        // UPDATE existing configuration - use PUT method with entity/configType endpoint
        response = await fetch(API_ENDPOINTS.taxConfig(editingConfig.entityId, editingConfig.configType), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...configData,
            configId: editingConfig.configId
          })
        });
      } else {
        // CREATE new configuration - use POST method
        response = await fetch(API_ENDPOINTS.taxConfig(), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(configData)
        });
      }
      
      const result = await response.json();
      
      if (result.success) {
        setShowCreateModal(false);
        setEditingConfig(null);
        loadTaxConfigurations();
        resetForm();
      } else {
        alert(`Failed to ${editingConfig ? 'update' : 'create'} tax configuration: ` + result.error);
      }
    } catch (error) {
      console.error('Error saving tax configuration:', error);
      alert(`Error ${editingConfig ? 'updating' : 'creating'} tax configuration`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfiguration = async (configId: string) => {
    if (!window.confirm('Are you sure you want to delete this tax configuration?')) return;
    
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.taxConfig()}/${configId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        loadTaxConfigurations();
      } else {
        alert('Failed to delete tax configuration: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting tax configuration:', error);
      alert('Error deleting tax configuration');
    }
  };

  const handleCalculateTax = async () => {
    if (!calculatorData.storeId || !calculatorData.amount) return;
    
    try {
      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.taxCalculate(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          entityId: calculatorData.storeId,
          configType: 'STORE',
          amount: calculatorData.amount,
          itemCategories: calculatorData.itemCategories
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setCalculationResult(result.data);
      } else {
        alert('Tax calculation failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error calculating tax:', error);
      alert('Error calculating tax');
    }
  };

  const resetForm = () => {
    setFormData({
      configType: 'STORE',
      entityId: '',
      entityName: '',
      taxRates: {
        salesTax: 0,
        cityTax: 0,
        countyTax: 0,
        stateTax: 0,
        specialTax: 0
      },
      taxJurisdiction: '',
      taxableCategories: [],
      exemptCategories: [],
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'US'
      },
      effectiveDate: new Date().toISOString().split('T')[0]
    });
  };

  const getTotalTaxRate = (taxRates: any): number => {
    return Object.values(taxRates).reduce((sum, rate) => (sum as number) + ((rate as number) || 0), 0) as number;
  };

  const getEntityIcon = (configType: string) => {
    switch (configType) {
      case 'ENTERPRISE': return <Building className="h-4 w-4" />;
      case 'ORG_UNIT': return <Building className="h-4 w-4" />;
      case 'STORE': return <MapPin className="h-4 w-4" />;
      default: return <Building className="h-4 w-4" />;
    }
  };

  if (loading && taxConfigurations.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading tax configurations...</span>
      </div>
    );
  }

  return (
    <div className="tax-management-container">
      {/* Tax Management UI - Fixed TypeScript issues */}
      <div className="tax-management-header mb-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600 mt-1">Configure tax rates for stores and organizational units</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Enterprise selection lives in the header strip — no in-page picker. */}

            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Tax Config</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mt-6">
          <button
            onClick={() => setActiveTab('configurations')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'configurations'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Building className="h-4 w-4 inline mr-2" />
            Tax Configurations
          </button>
          <button
            onClick={() => setActiveTab('calculator')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'calculator'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Calculator className="h-4 w-4 inline mr-2" />
            Tax Calculator
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'configurations' && (
        <div className="tax-configurations">
          {taxConfigurations.length === 0 ? (
            <div className="text-center py-12">
              <Percent className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Tax Configurations</h3>
              <p className="text-gray-600 mb-6">Get started by creating your first tax configuration</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
              >
                Create Tax Configuration
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {taxConfigurations.map((config: TaxConfiguration) => (
                <div key={config.configId} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        {getEntityIcon(config.configType)}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{config.entityName}</h3>
                          <p className="text-sm text-gray-600">
                            {config.configType.replace('_', ' ')} • {config.taxJurisdiction}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            config.validationStatus === 'VALID' ? 'bg-green-100 text-green-800' :
                            config.validationStatus === 'EXPIRED' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {config.validationStatus}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Tax Rates */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Tax Rates</h4>
                          <div className="space-y-1">
                            {Object.entries(config.taxRates).map(([type, rate]) => (
                              <div key={type} className="flex justify-between text-sm">
                                <span className="text-gray-600 capitalize">{type.replace('Tax', '')}:</span>
                                <span className="font-medium">{Number(rate).toFixed(2)}%</span>
                              </div>
                            ))}
                            <div className="border-t pt-1 mt-2">
                              <div className="flex justify-between text-sm font-semibold">
                                <span>Total:</span>
                                <span>{getTotalTaxRate(config.taxRates).toFixed(2)}%</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Taxable Categories */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Taxable Categories</h4>
                          <div className="flex flex-wrap gap-1">
                            {config.taxableCategories.map((category: string) => (
                              <span key={category} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                {category}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Address */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Address</h4>
                          {config.address ? (
                            <p className="text-sm text-gray-600">
                              {config.address.street}<br />
                              {config.address.city}, {config.address.state} {config.address.zipCode}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400">No address specified</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => {
                          setEditingConfig(config);
                          setFormData({
                            configType: config.configType,
                            entityId: config.entityId,
                            entityName: config.entityName,
                            taxRates: {
                              salesTax: config.taxRates?.salesTax || 0,
                              cityTax: config.taxRates?.cityTax || 0,
                              countyTax: config.taxRates?.countyTax || 0,
                              stateTax: config.taxRates?.stateTax || 0,
                              specialTax: config.taxRates?.specialTax || 0
                            },
                            taxJurisdiction: config.taxJurisdiction,
                            taxableCategories: config.taxableCategories,
                            exemptCategories: config.exemptCategories,
                            address: config.address || {
                              street: '',
                              city: '',
                              state: '',
                              zipCode: '',
                              country: 'US'
                            },
                            effectiveDate: config.effectiveDate.split('T')[0]
                          });
                          setShowCreateModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteConfiguration(config.configId)}
                        className="p-2 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'calculator' && (
        <div className="tax-calculator bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tax Calculator</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Store</label>
                <select
                  value={calculatorData.storeId}
                  onChange={(e) => setCalculatorData(prev => ({ ...prev, storeId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Store</option>
                  {stores.map((store: Store) => (
                    <option key={store.storeId} value={store.storeId}>
                      {store.name} {store.storeCode ? `(${store.storeCode})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={calculatorData.amount}
                  onChange={(e) => setCalculatorData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Item Categories</label>
                <div className="space-y-2">
                  {['food', 'beverage', 'alcohol', 'retail', 'service'].map((category: string) => (
                    <label key={category} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={calculatorData.itemCategories.includes(category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCalculatorData(prev => ({
                              ...prev,
                              itemCategories: [...prev.itemCategories, category]
                            }));
                          } else {
                            setCalculatorData(prev => ({
                              ...prev,
                              itemCategories: prev.itemCategories.filter(c => c !== category)
                            }));
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 capitalize">{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                onClick={handleCalculateTax}
                disabled={!calculatorData.storeId || !calculatorData.amount}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <Calculator className="h-4 w-4" />
                <span>Calculate Tax</span>
              </button>
            </div>

            {calculationResult && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Calculation Result</h4>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">${calculationResult.taxableAmount.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax Rate:</span>
                    <span className="font-medium">{calculationResult.taxRate.toFixed(2)}%</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax Amount:</span>
                    <span className="font-medium">${calculationResult.taxAmount.toFixed(2)}</span>
                  </div>
                  
                  <div className="border-t pt-2">
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total:</span>
                      <span>${calculationResult.totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <strong>Jurisdiction:</strong> {calculationResult.jurisdiction}
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <strong>Taxable:</strong> {calculationResult.isTaxable ? 'Yes' : 'No'}
                  </div>
                  
                  {calculationResult.breakdown && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-2">Tax Breakdown:</h5>
                      <div className="space-y-1">
                        {Object.entries(calculationResult.breakdown).map(([type, rate]) => (
                          <div key={type} className="flex justify-between text-sm">
                            <span className="text-gray-600 capitalize">{type.replace('Tax', '')}:</span>
                            <span>{`${Number(rate || 0).toFixed(2)}%`}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingConfig ? 'Edit Tax Configuration' : 'Create Tax Configuration'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingConfig(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Configuration Type</label>
                  <select
                    value={formData.configType}
                    onChange={(e) => setFormData(prev => ({ ...prev, configType: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ENTERPRISE">Enterprise</option>
                    <option value="ORG_UNIT">Organizational Unit</option>
                    <option value="STORE">Store</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Entity</label>
                  <select
                    value={formData.entityId}
                    onChange={(e) => {
                      const selectedStore = stores.find(s => s.storeId === e.target.value);
                      setFormData(prev => ({ 
                        ...prev, 
                        entityId: e.target.value,
                        entityName: selectedStore?.name || ''
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Entity</option>
                    {formData.configType === 'STORE' && stores.map((store: Store) => (
                      <option key={store.storeId} value={store.storeId}>
                        {store.name} {store.storeCode ? `(${store.storeCode})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tax Jurisdiction</label>
                  <input
                    type="text"
                    value={formData.taxJurisdiction}
                    onChange={(e) => setFormData(prev => ({ ...prev, taxJurisdiction: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., FL-PALMBEACH-NORTHPALMBEACH"
                  />
                </div>
              </div>

              {/* Tax Rates */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tax Rates (%)</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(formData.taxRates).map(([type, rate]) => (
                    <div key={type}>
                      <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                        {type.replace('Tax', '')} Tax
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={rate}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          taxRates: {
                            ...prev.taxRates,
                            [type]: parseFloat(e.target.value) || 0
                          }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Categories */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Taxable Categories</label>
                  <div className="space-y-2">
                    {['food', 'beverage', 'alcohol', 'retail', 'service'].map((category: string) => (
                      <label key={category} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.taxableCategories.includes(category)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                taxableCategories: [...prev.taxableCategories, category]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                taxableCategories: prev.taxableCategories.filter(c => c !== category)
                              }));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700 capitalize">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Exempt Categories</label>
                  <div className="space-y-2">
                    {['medical', 'nonprofit', 'education', 'government'].map((category: string) => (
                      <label key={category} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.exemptCategories.includes(category)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                exemptCategories: [...prev.exemptCategories, category]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                exemptCategories: prev.exemptCategories.filter(c => c !== category)
                              }));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700 capitalize">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                    <input
                      type="text"
                      value={formData.address.street}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        address: { ...prev.address, street: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      value={formData.address.city}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        address: { ...prev.address, city: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <input
                      type="text"
                      value={formData.address.state}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        address: { ...prev.address, state: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="FL"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                    <input
                      type="text"
                      value={formData.address.zipCode}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        address: { ...prev.address, zipCode: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="12345"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Effective Date</label>
                    <input
                      type="date"
                      value={formData.effectiveDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingConfig(null);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfiguration}
                disabled={saving || !formData.entityId || !selectedEnterprise}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Saving...' : (editingConfig ? 'Update' : 'Create')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxManagement;